// ============================================================
// Fee Setup Guide Banner (Flat & Compact)
// ============================================================
// Ultra-compact, flat horizontal guide banner for fee setup.

import { Box, Typography, Button, Paper, Stack, Chip } from '@mui/material';
import { CheckCircle2, ArrowLeft, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import { tokens } from '@/theme/tokens';

export function FeeSetupGuideBanner({ currentStep = 2, structureId, itemId }) {
  const navigate = useNavigate();

  const steps = [
    {
      step: 1,
      label: '1. Fee Plan',
      onClick: () => navigate(PATHS.FEES_STRUCTURE),
    },
    {
      step: 2,
      label: '2. Fee Components',
      onClick: structureId ? () => navigate(`/fees/structure/${structureId}/components`) : null,
    },
    {
      step: 3,
      label: '3. Installment Schedule',
      onClick: (structureId && itemId) ? () => navigate(`/fees/structure/${structureId}/components/${itemId}/installments`) : null,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        px: 2,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${tokens.colors.border}`,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Sparkles size={16} color={tokens.colors.primary.main} />
        <Typography variant="body2" fontWeight={700} color="text.primary">
          Fee Setup Guide:
        </Typography>
      </Box>

      {/* Stepper Pills Row */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {steps.map((item, idx) => {
          const isDone = item.step < currentStep;
          const isActive = item.step === currentStep;

          return (
            <Stack key={item.step} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Chip
                size="small"
                icon={isDone ? <CheckCircle2 size={13} /> : undefined}
                label={item.label}
                color={isActive ? 'primary' : isDone ? 'success' : 'default'}
                variant={isActive || isDone ? 'filled' : 'outlined'}
                onClick={isDone && item.onClick ? item.onClick : undefined}
                sx={{
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.75rem',
                  height: 26,
                  cursor: isDone && item.onClick ? 'pointer' : 'default',
                }}
              />
              {idx < steps.length - 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  →
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>

      {/* Back Button */}
      <Button
        size="small"
        variant="text"
        startIcon={<ArrowLeft size={14} />}
        onClick={() => {
          if (currentStep === 3 && structureId) {
            navigate(`/fees/structure/${structureId}/components`);
          } else {
            navigate(PATHS.FEES_STRUCTURE);
          }
        }}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'none',
          py: 0.25,
          px: 1,
        }}
      >
        {currentStep === 3 ? 'Back to Components' : 'Back to Fee Plans'}
      </Button>
    </Paper>
  );
}
